import {produce} from 'immer';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  AntibodyChainType,
  AntibodyDomainType,
  AntibodyFormatDomainTerminusStyle,
} from 'graphql-client/__generated/graphqlGlobalTypes';

export const getSingleDomainModule = (domainType: AntibodyDomainType): AntibodyFormat => ({
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 2,
      domainRefs: [1],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: domainType,
      label: domainType,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: domainType,
      specificity: null,
      label: domainType,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [2],
      domainRefsPerChain: [[1]],
      domains: [
        {
          type: domainType,
          specificity: null,
          label: domainType,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 1,
        translate: {
          x: 16,
          y: 16,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 96,
        svgViewboxWidth: 64,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
});

export const getTwoDomainModule = (
  domainType1: AntibodyDomainType,
  domainType2: AntibodyDomainType
): AntibodyFormat => ({
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 3,
      domainRefs: [1, 2],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: domainType1,
      label: domainType1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: domainType2,
      label: domainType2,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: domainType1,
      specificity: null,
      label: domainType1,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: domainType2,
      specificity: null,
      label: domainType2,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [3],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: domainType1,
          specificity: null,
          refs: [1],
          label: domainType1,
          __typename: 'DistinctDomainNode',
        },
        {
          type: domainType2,
          specificity: null,
          refs: [2],
          label: domainType2,
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 16,
          y: 84,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 16,
          y: 16,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 164,
        svgViewboxWidth: 64,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
});

export const FAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      label: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.VL,
      label: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.CL,
      label: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: null,
      label: AntibodyDomainType.CH1,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: null,
      label: AntibodyDomainType.CL,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [5],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: null,
          label: AntibodyDomainType.CH1,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [6],
      domainRefsPerChain: [[3, 4]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: null,
          label: AntibodyDomainType.CL,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 100,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 100,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 72,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 196,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const SCFV: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 4,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      label: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.L,
      label: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.VL,
      label: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: null,
      label: AntibodyDomainType.L,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [4],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: null,
          label: AntibodyDomainType.L,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 72,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 45.6,
          y: 103.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 139.2,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const SCFAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 6,
      domainRefs: [1, 2, 3, 4, 5],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      label: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.L,
      label: AntibodyDomainType.L,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.VL,
      label: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 5,
      type: AntibodyDomainType.CL,
      label: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: null,
      label: AntibodyDomainType.CH1,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: null,
      label: AntibodyDomainType.L,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: null,
      label: AntibodyDomainType.CL,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [6],
      domainRefsPerChain: [[1, 2, 3, 4, 5]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: null,
          label: AntibodyDomainType.CH1,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: null,
          label: AntibodyDomainType.L,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: null,
          label: AntibodyDomainType.CL,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 5,
        translate: {
          x: 72,
          y: 111.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 45.6,
          y: 175.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 132,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 111.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 43.2,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 207.2,
        svgViewboxWidth: 136,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const FUSION: AntibodyFormat = produce(getSingleDomainModule(AntibodyDomainType.F), (draft) => {
  draft.visualization.domainVisualizations = [
    {
      ref: 1,
      translate: {
        x: 16,
        y: 16,
        __typename: 'AntibodyFormatDomainTranslationNode',
      },
      rotate: 0,
      reflectX: false,
      reflectY: false,
      __typename: 'FusionDomainVisualizationNode',
    },
  ];
  return draft;
});

export const IGG: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 17,
      domainRefs: [1, 2, 3, 4, 5],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [9, 10, 11, 12, 13],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [6, 7],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 16,
      domainRefs: [14, 15],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.VH,
      label: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.CH2,
      label: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 5,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 6,
      type: AntibodyDomainType.VL,
      label: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 7,
      type: AntibodyDomainType.CL,
      label: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 9,
      type: AntibodyDomainType.VH,
      label: AntibodyDomainType.VH,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 10,
      type: AntibodyDomainType.CH1,
      label: AntibodyDomainType.CH1,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 11,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 12,
      type: AntibodyDomainType.CH2,
      label: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 13,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 14,
      type: AntibodyDomainType.VL,
      label: AntibodyDomainType.VL,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 15,
      type: AntibodyDomainType.CL,
      label: AntibodyDomainType.CL,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: null,
      label: AntibodyDomainType.VH,
      refs: [1, 9],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: null,
      label: AntibodyDomainType.CH1,
      refs: [2, 10],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [3, 11],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: null,
      label: AntibodyDomainType.CH2,
      refs: [4, 12],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      refs: [5, 13],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: null,
      label: AntibodyDomainType.VL,
      refs: [6, 14],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: null,
      label: AntibodyDomainType.CL,
      refs: [7, 15],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [17, 18],
      domainRefsPerChain: [
        [1, 2, 3, 4, 5],
        [9, 10, 11, 12, 13],
      ],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: null,
          label: AntibodyDomainType.VH,
          refs: [1, 9],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: null,
          label: AntibodyDomainType.CH1,
          refs: [2, 10],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [3, 11],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: null,
          label: AntibodyDomainType.CH2,
          refs: [4, 12],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [5, 13],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [8, 16],
      domainRefsPerChain: [
        [6, 7],
        [14, 15],
      ],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: null,
          label: AntibodyDomainType.VL,
          refs: [6, 14],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: null,
          label: AntibodyDomainType.CL,
          refs: [7, 15],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 15],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 11],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [4, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [5, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 5,
        translate: {
          x: 128.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 128.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 140.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.35133518344378,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 168.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 168.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 180.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 190.06089264212847,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 233.77045010081312,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 32,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 75.70955745868467,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 264.41222782557224,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 220.70267036688756,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 324.09102213209053,
        svgViewboxWidth: 328.41222782557224,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_IGG: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 17,
      domainRefs: [1, 2, 3, 4, 5],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 18,
      domainRefs: [9, 10, 11, 12, 13],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [6, 7],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 16,
      domainRefs: [14, 15],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 4,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 5,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 6,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 7,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 9,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 10,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 11,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 12,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 13,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 14,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 15,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'a',
      label: `${AntibodyDomainType.CH1}a`,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'a',
      label: `${AntibodyDomainType.CH2}a`,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [6],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'a',
      label: `${AntibodyDomainType.CL}a`,
      refs: [7],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [9],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'b',
      label: `${AntibodyDomainType.CH1}b`,
      refs: [10],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      refs: [11],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'b',
      label: `${AntibodyDomainType.CH2}b`,
      refs: [12],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      refs: [13],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [14],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'b',
      label: `${AntibodyDomainType.CL}b`,
      refs: [15],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [17],
      domainRefsPerChain: [[1, 2, 3, 4, 5]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          label: `${AntibodyDomainType.VH}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'a',
          label: `${AntibodyDomainType.CH1}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'a',
          label: `${AntibodyDomainType.CH2}a`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [18],
      domainRefsPerChain: [[9, 10, 11, 12, 13]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          label: `${AntibodyDomainType.VH}b`,
          refs: [9],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'b',
          label: `${AntibodyDomainType.CH1}b`,
          refs: [10],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [11],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'b',
          label: `${AntibodyDomainType.CH2}b`,
          refs: [12],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [13],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [8],
      domainRefsPerChain: [[6, 7]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          label: `${AntibodyDomainType.VL}a`,
          refs: [6],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'a',
          label: `${AntibodyDomainType.CL}a`,
          refs: [7],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [16],
      domainRefsPerChain: [[14, 15]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          label: `${AntibodyDomainType.VL}b`,
          refs: [14],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'b',
          label: `${AntibodyDomainType.CL}b`,
          refs: [15],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 7],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 15],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 11],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [4, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [5, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 5,
        translate: {
          x: 128.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 128.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 140.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.35133518344378,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 168.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 168.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 180.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 190.06089264212847,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 233.77045010081312,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 32,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 75.70955745868467,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 264.41222782557224,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 220.70267036688756,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 324.09102213209053,
        svgViewboxWidth: 328.41222782557224,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 7,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [4, 5, 6],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH2,
      label: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 5,
      type: AntibodyDomainType.CH2,
      label: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 6,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [1, 4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: null,
      label: AntibodyDomainType.CH2,
      refs: [2, 5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      refs: [3, 6],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [7, 8],
      domainRefsPerChain: [
        [1, 2, 3],
        [4, 5, 6],
      ],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: null,
          label: AntibodyDomainType.CH2,
          refs: [2, 5],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [3, 6],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 5],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 72,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 84.14327434188075,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 7,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 8,
      domainRefs: [4, 5, 6],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 4,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 5,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 6,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'a',
      label: `${AntibodyDomainType.CH2}a`,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'b',
      label: `${AntibodyDomainType.CH2}b`,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      refs: [6],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [7],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'a',
          label: `${AntibodyDomainType.CH2}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [8],
      domainRefsPerChain: [[4, 5, 6]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'b',
          label: `${AntibodyDomainType.CH2}b`,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [6],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 5],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [3, 6],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 72,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 84.14327434188075,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const MONO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 4,
      domainRefs: [1, 2, 3],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH2,
      label: AntibodyDomainType.CH2,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: null,
      label: AntibodyDomainType.CH2,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [4],
      domainRefsPerChain: [[1, 2, 3]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: null,
          label: AntibodyDomainType.CH2,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 3,
        translate: {
          x: 32,
          y: 125.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 221.19253331742772,
        svgViewboxWidth: 108.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 3,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 4,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [1, 3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      refs: [2, 4],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [5, 6],
      domainRefsPerChain: [
        [1, 2],
        [3, 4],
      ],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [2, 4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 84.14327434188075,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HINGE: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 3,
      domainRefs: [1],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 4,
      domainRefs: [2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [1, 2],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [3, 4],
      domainRefsPerChain: [[1], [2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1, 2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 2],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 1,
        translate: {
          x: 32,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 72,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 96,
        svgViewboxWidth: 112,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const MONO_CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 3,
      domainRefs: [1, 2],
      type: AntibodyChainType.NONE,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: null,
      ref: 1,
      type: AntibodyDomainType.H,
      label: AntibodyDomainType.H,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: null,
      ref: 2,
      type: AntibodyDomainType.CH3,
      label: AntibodyDomainType.CH3,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: null,
      label: AntibodyDomainType.H,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: null,
      label: AntibodyDomainType.CH3,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.NONE,
      label: 'Chain',
      refs: [3],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: null,
          label: AntibodyDomainType.H,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: null,
          label: AntibodyDomainType.CH3,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 108.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_CH3: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 5,
      domainRefs: [1, 2],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 6,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 3,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 4,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 1',
      refs: [5],
      domainRefsPerChain: [[1, 2]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light 2',
      refs: [6],
      domainRefsPerChain: [[3, 4]],
      domains: [
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 2,
        translate: {
          x: 32,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 44.14327434188076,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 72,
          y: 57.19253331742773,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 84.14327434188075,
          y: 31.999999999999996,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 153.19253331742772,
        svgViewboxWidth: 148.14327434188075,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const FAB_SCFV_HETERO_FC: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 15,
      domainRefs: [1, 2, 9, 10, 11],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 16,
      domainRefs: [6, 7, 8, 12, 13, 14],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 5,
      domainRefs: [3, 4],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 4,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 6,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 7,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 8,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 9,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 10,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 11,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 12,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 13,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 14,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'a',
      label: `${AntibodyDomainType.CH1}a`,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'a',
      label: `${AntibodyDomainType.CL}a`,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [6],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'b',
      label: `${AntibodyDomainType.L}b`,
      refs: [7],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [8],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      refs: [9],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'a',
      label: `${AntibodyDomainType.CH2}a`,
      refs: [10],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      refs: [11],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      refs: [12],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'b',
      label: `${AntibodyDomainType.CH2}b`,
      refs: [13],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      refs: [14],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [15],
      domainRefsPerChain: [[1, 2, 9, 10, 11]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          label: `${AntibodyDomainType.VH}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'a',
          label: `${AntibodyDomainType.CH1}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [9],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'a',
          label: `${AntibodyDomainType.CH2}a`,
          refs: [10],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [11],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [16],
      domainRefsPerChain: [[6, 7, 8, 12, 13, 14]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          label: `${AntibodyDomainType.VH}b`,
          refs: [6],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'b',
          label: `${AntibodyDomainType.L}b`,
          refs: [7],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          label: `${AntibodyDomainType.VL}b`,
          refs: [8],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [12],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'b',
          label: `${AntibodyDomainType.CH2}b`,
          refs: [13],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [14],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [5],
      domainRefsPerChain: [[3, 4]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          label: `${AntibodyDomainType.VL}a`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'a',
          label: `${AntibodyDomainType.CL}a`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [1, 3],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [2, 4],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 13],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [11, 14],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 11,
        translate: {
          x: 128.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 128.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 140.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 106.35133518344378,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 62.64177772475912,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 168.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 168.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 180.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 190.06089264212847,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 198.981781504508,
          y: 156.946774325821,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: true,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 220.70267036688756,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 32,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 75.70955745868467,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 324.09102213209053,
        svgViewboxWidth: 284.70267036688756,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const HETERO_IGG_SCFAB: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 23,
      domainRefs: [6, 7, 8, 9, 10, 22, 1, 2, 3, 4, 5],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 24,
      domainRefs: [14, 15, 16, 17, 18],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 13,
      domainRefs: [11, 12],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 21,
      domainRefs: [19, 20],
      type: AntibodyChainType.LIGHT,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 4,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 5,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 6,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 7,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 8,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 9,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 10,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 11,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 12,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 14,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 15,
      type: AntibodyDomainType.CH1,
      label: `${AntibodyDomainType.CH1}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 16,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 17,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 18,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 19,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'c',
      ref: 20,
      type: AntibodyDomainType.CL,
      label: `${AntibodyDomainType.CL}c`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 22,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'a',
      label: `${AntibodyDomainType.CH1}a`,
      refs: [2],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'a',
      label: `${AntibodyDomainType.L}a`,
      refs: [3],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'a',
      label: `${AntibodyDomainType.CL}a`,
      refs: [5],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [6],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'b',
      label: `${AntibodyDomainType.CH1}b`,
      refs: [7],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'b',
      label: `${AntibodyDomainType.H}b`,
      refs: [8],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'b',
      label: `${AntibodyDomainType.CH2}b`,
      refs: [9],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'b',
      label: `${AntibodyDomainType.CH3}b`,
      refs: [10],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [11],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'b',
      label: `${AntibodyDomainType.CL}b`,
      refs: [12],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'c',
      label: `${AntibodyDomainType.VH}c`,
      refs: [14],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH1,
      specificity: 'c',
      label: `${AntibodyDomainType.CH1}c`,
      refs: [15],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'c',
      label: `${AntibodyDomainType.H}c`,
      refs: [16],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'c',
      label: `${AntibodyDomainType.CH2}c`,
      refs: [17],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'c',
      label: `${AntibodyDomainType.CH3}c`,
      refs: [18],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'c',
      label: `${AntibodyDomainType.VL}c`,
      refs: [19],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CL,
      specificity: 'c',
      label: `${AntibodyDomainType.CL}c`,
      refs: [20],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'b',
      label: `${AntibodyDomainType.L}b`,
      refs: [22],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 1',
      refs: [23],
      domainRefsPerChain: [[6, 7, 8, 9, 10, 22, 1, 2, 3, 4, 5]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          label: `${AntibodyDomainType.VH}b`,
          refs: [6],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'b',
          label: `${AntibodyDomainType.CH1}b`,
          refs: [7],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'b',
          label: `${AntibodyDomainType.H}b`,
          refs: [8],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'b',
          label: `${AntibodyDomainType.CH2}b`,
          refs: [9],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'b',
          label: `${AntibodyDomainType.CH3}b`,
          refs: [10],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'b',
          label: `${AntibodyDomainType.L}b`,
          refs: [22],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          label: `${AntibodyDomainType.VH}a`,
          refs: [1],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'a',
          label: `${AntibodyDomainType.CH1}a`,
          refs: [2],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'a',
          label: `${AntibodyDomainType.L}a`,
          refs: [3],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          label: `${AntibodyDomainType.VL}a`,
          refs: [4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'a',
          label: `${AntibodyDomainType.CL}a`,
          refs: [5],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy 2',
      refs: [24],
      domainRefsPerChain: [[14, 15, 16, 17, 18]],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'c',
          label: `${AntibodyDomainType.VH}c`,
          refs: [14],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH1,
          specificity: 'c',
          label: `${AntibodyDomainType.CH1}c`,
          refs: [15],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'c',
          label: `${AntibodyDomainType.H}c`,
          refs: [16],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'c',
          label: `${AntibodyDomainType.CH2}c`,
          refs: [17],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'c',
          label: `${AntibodyDomainType.CH3}c`,
          refs: [18],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [13],
      domainRefsPerChain: [[11, 12]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          label: `${AntibodyDomainType.VL}b`,
          refs: [11],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'b',
          label: `${AntibodyDomainType.CL}b`,
          refs: [12],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
    {
      type: AntibodyChainType.LIGHT,
      label: 'Light',
      refs: [21],
      domainRefsPerChain: [[19, 20]],
      domains: [
        {
          type: AntibodyDomainType.VL,
          specificity: 'c',
          label: `${AntibodyDomainType.VL}c`,
          refs: [19],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CL,
          specificity: 'c',
          label: `${AntibodyDomainType.CL}c`,
          refs: [20],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [6, 11],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [7, 12],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [14, 19],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [15, 20],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [8, 16],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [9, 17],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [10, 18],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 22,
        translate: {
          x: 141.27872454811495,
          y: 278.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 40,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 128.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 128.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 140.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 106.35133518344378,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 62.64177772475912,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 106.35133518344378,
          y: 304.09102213209053,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 62.64177772475912,
          y: 356.182044264181,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 62.7756675917219,
          y: 411.280781004405,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: true,
        reflectY: false,
        centerLength: 132,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 75.70955745868467,
          y: 278.3795177446289,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 32,
          y: 330.47053987671944,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 18,
        translate: {
          x: 168.20611391278612,
          y: 228.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 168.20611391278612,
          y: 160.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 180.34938825466688,
          y: 134.89848881466276,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 190.06089264212847,
          y: 84.0910221320905,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 233.77045010081312,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 32,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 75.70955745868467,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 19,
        translate: {
          x: 264.41222782557224,
          y: 57.71150438746156,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 20,
        translate: {
          x: 220.70267036688756,
          y: 109.80252651955207,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 452.182044264181,
        svgViewboxWidth: 328.41222782557224,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};

export const SCFV_FC_SCFV: AntibodyFormat = {
  id: 'placeholder',
  name: 'placeholder',
  chains: [
    {
      ref: 21,
      domainRefs: [1, 2, 3, 13, 14, 15, 19, 7, 8, 9],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
    {
      ref: 22,
      domainRefs: [4, 5, 6, 16, 17, 18, 20, 10, 11, 12],
      type: AntibodyChainType.HEAVY,
      __typename: 'AntibodyFormatChainNode',
    },
  ],
  domains: [
    {
      specificity: 'a',
      ref: 1,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 2,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 3,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 4,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 5,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 6,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 7,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 8,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 9,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 10,
      type: AntibodyDomainType.VH,
      label: `${AntibodyDomainType.VH}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 11,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'b',
      ref: 12,
      type: AntibodyDomainType.VL,
      label: `${AntibodyDomainType.VL}b`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 13,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 14,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 15,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 16,
      type: AntibodyDomainType.H,
      label: `${AntibodyDomainType.H}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 17,
      type: AntibodyDomainType.CH2,
      label: `${AntibodyDomainType.CH2}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 18,
      type: AntibodyDomainType.CH3,
      label: `${AntibodyDomainType.CH3}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 19,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
    {
      specificity: 'a',
      ref: 20,
      type: AntibodyDomainType.L,
      label: `${AntibodyDomainType.L}a`,
      __typename: 'AntibodyFormatDomainNode',
    },
  ],
  distinctDomains: [
    {
      type: AntibodyDomainType.VH,
      specificity: 'a',
      label: `${AntibodyDomainType.VH}a`,
      refs: [1, 4],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'a',
      label: `${AntibodyDomainType.L}a`,
      refs: [2, 5, 19, 20],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'a',
      label: `${AntibodyDomainType.VL}a`,
      refs: [3, 6],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VH,
      specificity: 'b',
      label: `${AntibodyDomainType.VH}b`,
      refs: [7, 10],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.L,
      specificity: 'b',
      label: `${AntibodyDomainType.L}b`,
      refs: [8, 11],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.VL,
      specificity: 'b',
      label: `${AntibodyDomainType.VL}b`,
      refs: [9, 12],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.H,
      specificity: 'a',
      label: `${AntibodyDomainType.H}a`,
      refs: [13, 16],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH2,
      specificity: 'a',
      label: `${AntibodyDomainType.CH2}a`,
      refs: [14, 17],
      __typename: 'DistinctDomainNode',
    },
    {
      type: AntibodyDomainType.CH3,
      specificity: 'a',
      label: `${AntibodyDomainType.CH3}a`,
      refs: [15, 18],
      __typename: 'DistinctDomainNode',
    },
  ],
  distinctChains: [
    {
      type: AntibodyChainType.HEAVY,
      label: 'Heavy',
      refs: [21, 22],
      domainRefsPerChain: [
        [1, 2, 3, 13, 14, 15, 19, 7, 8, 9],
        [4, 5, 6, 16, 17, 18, 20, 10, 11, 12],
      ],
      domains: [
        {
          type: AntibodyDomainType.VH,
          specificity: 'a',
          label: `${AntibodyDomainType.VH}a`,
          refs: [1, 4],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'a',
          label: `${AntibodyDomainType.L}a`,
          refs: [2, 5, 19, 20],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'a',
          label: `${AntibodyDomainType.VL}a`,
          refs: [3, 6],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.H,
          specificity: 'a',
          label: `${AntibodyDomainType.H}a`,
          refs: [13, 16],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH2,
          specificity: 'a',
          label: `${AntibodyDomainType.CH2}a`,
          refs: [14, 17],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.CH3,
          specificity: 'a',
          label: `${AntibodyDomainType.CH3}a`,
          refs: [15, 18],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'a',
          label: `${AntibodyDomainType.L}a`,
          refs: [2, 5, 19, 20],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VH,
          specificity: 'b',
          label: `${AntibodyDomainType.VH}b`,
          refs: [7, 10],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.L,
          specificity: 'b',
          label: `${AntibodyDomainType.L}b`,
          refs: [8, 11],
          __typename: 'DistinctDomainNode',
        },
        {
          type: AntibodyDomainType.VL,
          specificity: 'b',
          label: `${AntibodyDomainType.VL}b`,
          refs: [9, 12],
          __typename: 'DistinctDomainNode',
        },
      ],
      __typename: 'DistinctChainNode',
    },
  ],
  interactions: [
    {
      domainRefs: [13, 16],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [14, 17],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
    {
      domainRefs: [15, 18],
      __typename: 'AntibodyFormatDomainInteractionNode',
    },
  ],
  visualization: {
    domainVisualizations: [
      {
        ref: 19,
        translate: {
          x: 97.56916708943028,
          y: 226,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 40,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 15,
        translate: {
          x: 84.49655645410145,
          y: 176,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 14,
        translate: {
          x: 84.49655645410145,
          y: 108,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 13,
        translate: {
          x: 96.63983079598222,
          y: 82.80746668257227,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 3,
        translate: {
          x: 62.641777724759116,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 2,
        translate: {
          x: 40.920888862379556,
          y: 104.8557521937308,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 1,
        translate: {
          x: 32,
          y: 57.711504387461574,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 7,
        translate: {
          x: 62.641777724759116,
          y: 252,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 8,
        translate: {
          x: 40.920888862379556,
          y: 299.1442478062692,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: true,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 9,
        translate: {
          x: 32,
          y: 226.28849561253844,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 20,
        translate: {
          x: 143.4239458187726,
          y: 226,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        centerLength: 40,
        __typename: 'StraightLineLinkerDomainVisualizationNode',
      },
      {
        ref: 18,
        translate: {
          x: 124.49655645410145,
          y: 176,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 17,
        translate: {
          x: 124.49655645410145,
          y: 108,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 16,
        translate: {
          x: 136.63983079598222,
          y: 82.80746668257227,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 0,
        reflectX: true,
        reflectY: false,
        angle: 140,
        freeEndLength: 12,
        fixedEndLength: 16,
        __typename: 'HingeDomainVisualizationNode',
      },
      {
        ref: 6,
        translate: {
          x: 146.35133518344378,
          y: 32,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 5,
        translate: {
          x: 155.27222404582335,
          y: 104.8557521937308,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: true,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 4,
        translate: {
          x: 176.9931129082029,
          y: 57.711504387461574,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: 40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 10,
        translate: {
          x: 146.35133518344378,
          y: 252,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
      {
        ref: 11,
        translate: {
          x: 155.27222404582335,
          y: 299.1442478062692,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        centerLength: 64,
        endRadius: 11.2,
        tipLength: 4,
        __typename: 'LinkerDomainVisualizationNode',
      },
      {
        ref: 12,
        translate: {
          x: 176.9931129082029,
          y: 226.28849561253844,
          __typename: 'AntibodyFormatDomainTranslationNode',
        },
        rotate: -40,
        reflectX: false,
        reflectY: false,
        nTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        cTerminusStyle: AntibodyFormatDomainTerminusStyle.FLAT,
        __typename: 'BlockDomainVisualizationNode',
      },
    ],
    sizing: {
      chainDomainSizing: {
        cornerRadius: 4,
        height: 64,
        width: 32,
        __typename: 'AntibodyFormatChainDomainSizingNode',
      },
      connectorSizing: {
        strokeThickness: 6,
        __typename: 'AntibodyFormatConnectorSizingNode',
      },
      svgSizing: {
        svgViewboxHeight: 348,
        svgViewboxWidth: 240.9931129082029,
        __typename: 'AntibodyFormatSvgSizingNode',
      },
      __typename: 'AntibodyFormatSizingNode',
    },
    __typename: 'AntibodyFormatVisualizationNode',
  },
  __typename: 'AntibodyFormatNode',
};
